/**
 * Direct-to-API data layer. greenbook is pure-static (no proxy server): this module keeps
 * the old `apiGet(path)` / `apiPost(path, body)` interface — so every hook in queries.ts and
 * every component is untouched — but instead of hitting a `/api/*` proxy it dispatches each
 * path to a `@greenbook/client` resource call against the live OpenMarkets API, using the
 * user's own key. The route table below mirrors the server proxy that used to live in
 * apps/web/server/proxy.ts.
 *
 * Responses still surface as `{ data, pagination?, meta? }`; errors as {@link ApiError}
 * (mapped from the client's `OpenMarketsError` so existing `err.code` handling keeps working).
 * Rate-limit headers flow into the status store via the client's `onRateLimit` callback.
 */
import { useSyncExternalStore } from "react";
import { OpenMarketsError, type RateLimit, type Result } from "@greenbook/client";
import { getClient, fetchAllPositions, NoCredentialError, withSignal } from "./client.js";
import { isPublicAppPath, PUBLIC_UNAVAILABLE, SIGN_IN_REQUIRED } from "./publicFeed.js";
import { getOrderSettings } from "./settings.js";
import { authIdentity, onAuthRejected } from "./omauth.js";

export interface Pagination {
  next_cursor: string | null;
  has_more: boolean;
  limit: number;
}

export interface Envelope<T> {
  data: T;
  pagination?: Pagination;
  meta?: { timestamp?: string; api_version?: string };
}

export interface RateSnapshot {
  limitMinute: number | null;
  remainingMinute: number | null;
  resetMinute: number | null;
  limitDay: number | null;
  remainingDay: number | null;
  resetDay: number | null;
}

export type CacheStatus = "hit" | "miss" | "stale";

export interface StatusState {
  rate: RateSnapshot | null;
  cache: CacheStatus | null;
  staleAgeSec: number | null;
  lastUpdated: number | null;
}

export class ApiError extends Error {
  code: string;
  status: number;
  details: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/* --------------------------------------------------------------- status store */

let state: StatusState = {
  rate: null,
  cache: null,
  staleAgeSec: null,
  lastUpdated: null,
};
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/**
 * Fed by the client's `onRateLimit` callback (see client.ts) on every response, so the
 * top-bar budget meter keeps rendering. There's no server cache anymore, so `cache` stays
 * null and the (inert) stale badge never shows.
 */
export function pushRateLimit(rl: RateLimit | null): void {
  const rate: RateSnapshot | null = rl
    ? {
        limitMinute: rl.limitMinute,
        remainingMinute: rl.remainingMinute,
        resetMinute: rl.resetMinute,
        limitDay: rl.limitDay,
        remainingDay: rl.remainingDay,
        resetDay: rl.resetDay,
      }
    : state.rate;
  state = { rate, cache: null, staleAgeSec: null, lastUpdated: Date.now() };
  emit();
}

export const statusStore = {
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  getSnapshot(): StatusState {
    return state;
  },
};

export function useStatus(): StatusState {
  return useSyncExternalStore(statusStore.subscribe, statusStore.getSnapshot);
}

/* ------------------------------------------------------------------ helpers */

const num = (v: string | null): number | undefined => (v != null && v !== "" ? Number(v) : undefined);
const str = (v: string | null): string | undefined => (v || undefined);

/** Map a client `Result` onto the browser `Envelope` the hooks expect. */
function env<T>(r: Result<T>): Envelope<T> {
  const out: Envelope<T> = { data: r.data };
  if (r.pagination) out.pagination = r.pagination;
  if (r.meta) out.meta = r.meta;
  return out;
}

/**
 * Convert any thrown error into the `ApiError` contract the UI branches on (`err.code`,
 * `instanceof ApiError`). A rejected/expired key (401) also clears the connected token so
 * the app falls back to the connect gate instead of looping.
 */
function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  if (err instanceof NoCredentialError) return new ApiError(401, "no_credential", err.message, {});
  if (err instanceof OpenMarketsError) {
    // The signed-out transport's synthetic 424 is "the feed isn't here" — present it as a 503.
    if (err.code === PUBLIC_UNAVAILABLE) return new ApiError(503, PUBLIC_UNAVAILABLE, err.message, err.details ?? {});
    // status 0 = network/validation → present as 502 like the old proxy did.
    const status = err.status && err.status >= 400 ? err.status : err.status === 0 ? 502 : err.status;
    return new ApiError(status, err.code, err.message, err.details ?? {});
  }
  return new ApiError(500, "client_error", err instanceof Error ? err.message : String(err), {});
}

/* ------------------------------------------------------------------ orders */

// Order placement and cancels are NOT routed here any more: they go through the official
// order SDK (lib/orderClient.ts), which sends an explicit `mode` on every leg, refuses a real
// leg unless greenbook armed real money (global Real + trade:execute), and validates against
// the server-served venue rules. The old client-side paper guard lived here.

/* ------------------------------------------------------------------- router */

/**
 * GET router — dispatches a proxy-style path onto a client resource call. Ordering matters
 * for the `/orders/*` and `/contests/:id/*` families (most specific first), exactly as the
 * Hono proxy registered them.
 */
async function routeGet<T>(pathname: string, q: URLSearchParams): Promise<Envelope<T>> {
  const c = getClient();
  const seg = pathname.split("/").filter(Boolean); // e.g. ["contests","abc","markets"]

  // Client-side settings surfaced under the old /config path (was server env flags).
  if (pathname === "/config") {
    const s = getOrderSettings();
    return { data: { enable_limit_orders: s.enable_limit_orders } as T };
  }

  switch (seg[0]) {
    case "auth":
      if (seg[1] === "me") return env((await c.auth.me()) as Result<T>);
      break;
    case "config":
      break; // handled above
    case "performance":
      return env((await c.auth.performance({ group_by: str(q.get("group_by")), from: str(q.get("from")), to: str(q.get("to")) })) as Result<T>);
    case "leagues":
      if (seg[1]) return env((await c.leagues.get(seg[1])) as Result<T>);
      return env((await c.leagues.list({ limit: 200 })) as Result<T>);
    case "participants":
      return env((await c.participants.list({ league_id: q.get("league_id") ?? "", limit: num(q.get("limit")), cursor: str(q.get("cursor")) })) as Result<T>);
    case "partners":
      return env((await c.partners.list()) as Result<T>);
    case "opportunities":
      if (seg[1]) return env((await c.opportunities.get(seg[1])) as Result<T>);
      return env((await c.opportunities.list({
            limit: num(q.get("limit")),
            status: str(q.get("status")),
            opportunity_type: str(q.get("opportunity_type")),
            model_id: str(q.get("model_id")),
            cursor: str(q.get("cursor")),
          })) as Result<T>);
    case "models":
      if (seg[1]) return env((await c.models.get(decodeURIComponent(seg[1]))) as Result<T>);
      return env((await c.models.list({
            status: str(q.get("status")),
            include_archived: q.get("include_archived") === "true" ? true : undefined,
          })) as Result<T>);
    case "signals":
      return env(
        (await c.signals.list({
          contest_id: str(q.get("contest_id")),
          league_id: str(q.get("league_id")),
          signal_type: str(q.get("signal_type")),
          status: str(q.get("status")),
          min_strength: num(q.get("min_strength")),
          limit: num(q.get("limit")),
          cursor: str(q.get("cursor")),
        })) as Result<T>,
      );
    case "arbitrages":
      return env(
        (await c.arbitrages.list({
          contest_id: str(q.get("contest_id")),
          league_id: str(q.get("league_id")),
          market_id: str(q.get("market_id")),
          partner_id: str(q.get("partner_id")),
          min_roi: num(q.get("min_roi")),
          after_fee: q.get("after_fee") === "true" ? true : undefined,
          min_after_fee_roi: num(q.get("min_after_fee_roi")),
          min_investment: num(q.get("min_investment")),
          sort: str(q.get("sort")),
          limit: num(q.get("limit")),
        })) as Result<T>,
      );
    case "depth":
      if (seg[1]) return env((await c.depth.getDepth(decodeURIComponent(seg[1]))) as Result<T>);
      break;
    case "orders":
      if (seg[1] === "resting") return env((await c.orders.resting()) as Result<T>);
      if (seg[1] && seg[2] === "resolution") return env((await c.orders.resolution(decodeURIComponent(seg[1]))) as Result<T>);
      if (!seg[1])
        return env(
          (await c.orders.list({
            status: str(q.get("status")),
            contest_id: str(q.get("contest_id")),
            settled: q.get("settled") === "true" ? true : undefined,
            result: str(q.get("result")),
            currency: str(q.get("currency")),
            mm: q.get("mm") === "true" ? true : undefined,
            model_id: str(q.get("model_id")),
            opportunity_type: str(q.get("opportunity_type")),
            sort: str(q.get("sort")),
            from: str(q.get("from")),
            to: str(q.get("to")),
            limit: num(q.get("limit")),
            cursor: str(q.get("cursor")),
          })) as Result<T>,
        );
      break;
    case "account":
      if (seg[1] === "partners") return env(stripCredentials(await c.account.partners()) as Result<T>);
      if (seg[1] === "balances") return env((await c.account.balances()) as Result<T>);
      if (seg[1] === "history") return env((await c.account.history({ limit: num(q.get("limit")), cursor: str(q.get("cursor")) })) as Result<T>);
      if (seg[1] === "positions") return env((await fetchAllPositions(str(q.get("status")))) as Result<T>);
      break;
    case "contests":
      if (!seg[1]) {
        return env(
          (await c.contests.list({ league_id: str(q.get("league_id")), status: str(q.get("status")), q: str(q.get("q")), limit: num(q.get("limit")), cursor: str(q.get("cursor")) })) as Result<T>,
        );
      }
      if (seg[2] === "markets") return env((await c.markets.contestMarkets(seg[1])) as Result<T>);
      if (seg[2] === "liquidity") return env((await c.liquidity.getContestLiquidity(seg[1])) as Result<T>);
      if (seg[2] === "state") return env((await c.contestState.get(seg[1])) as Result<T>);
      if (seg[2] === "price-history")
        return env(
          (await c.contests.priceHistory(seg[1], {
            resolution: str(q.get("resolution")),
            from: num(q.get("from")),
            to: num(q.get("to")),
            limit: num(q.get("limit")),
            position_hash: str(q.get("position_hash")),
            partner_id: str(q.get("partner_id")),
          })) as Result<T>,
        );
      return env((await c.contests.get(seg[1])) as Result<T>);
  }
  throw new ApiError(404, "not_found", `No route for GET ${pathname}`, {});
}

/** POST router — writes (never cached). Orders are not here: see lib/orderClient.ts. */
async function routePost<T>(pathname: string, body: unknown, headers?: Record<string, string>): Promise<Envelope<T>> {
  const c = getClient();

  // Creating a model writes to the account (needs trade:execute). The body goes as-is so the
  // API stays the authority on its strict contract (unknown fields → 400 unknown_parameter).
  if (pathname === "/models") {
    const b = (body ?? {}) as { model_name: string; model_description?: string; league_id?: string };
    return env((await c.models.create(b, { idempotencyKey: headers?.["Idempotency-Key"] })) as Result<T>);
  }

  if (pathname === "/contest-state/batch") {
    const ids = Array.isArray((body as { contest_ids?: unknown })?.contest_ids)
      ? (body as { contest_ids: unknown[] }).contest_ids.map(String)
      : [];
    return env((await c.contestState.batch(ids)) as Result<T>);
  }

  if (pathname === "/account/refresh-balances") {
    return env(stripCredentials(await c.account.refreshBalances()) as Result<T>);
  }

  if (pathname === "/account/sync-history") {
    return env((await c.account.syncHistory()) as Result<T>);
  }

  throw new ApiError(404, "not_found", `No route for POST ${pathname}`, {});
}

/* ------------------------------------------------- credential stripping */

const CREDENTIAL_KEYS = new Set(["encrypted_credentials", "credentials", "credential_payload", "encrypted_credential", "secret"]);

/** Deep-clone dropping any credential-ish key (matches the old proxy; keeps the UI clean). */
function stripCredentials<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => stripCredentials(v)) as unknown as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (CREDENTIAL_KEYS.has(k)) continue;
      out[k] = stripCredentials(v);
    }
    return out as T;
  }
  return value;
}

/* -------------------------------------------------------------------- public API */

/** Codes WE produce for signed-out visitors — never an API verdict on a credential. */
const SIGNED_OUT_CODES = new Set<string>(["no_credential", SIGN_IN_REQUIRED, PUBLIC_UNAVAILABLE]);

/**
 * True when the error just means "this needs a signed-in user" — an off-list path for the
 * public feed, the feed not being configured here, or no credential at all. The UI turns these
 * into a "Sign in to see this" card rather than an error box.
 */
export function isSignInRequired(err: unknown): boolean {
  return err instanceof ApiError && SIGNED_OUT_CODES.has(err.code);
}

/** 403 codes a plan upgrade or a fresh consent can fix (docs: "When a user's plan doesn't include it"). */
export const ENTITLEMENT_CODES = new Set(["entitlement_required", "scope_not_granted", "real_money_not_permitted"]);

export interface EntitlementInfo {
  code: string;
  /** Capability codes the API said were missing (`details.missing`). */
  missing: string[];
  /** `details.upgrade` — present only when some plan sells it. */
  upgrade: { label?: string; price_label?: string; url?: string } | null;
}

/** The entitlement refusal an error describes, or null. */
export function entitlementOf(err: unknown): EntitlementInfo | null {
  if (!(err instanceof ApiError) || err.status !== 403 || !ENTITLEMENT_CODES.has(err.code)) return null;
  const d = err.details ?? {};
  const missing = Array.isArray(d.missing) ? d.missing.map(String) : [];
  const up = d.upgrade && typeof d.upgrade === "object" ? (d.upgrade as EntitlementInfo["upgrade"]) : null;
  return { code: err.code, missing, upgrade: up };
}

/**
 * A real API 401 (revoked from the dashboard, uninstalled, or a key that stopped working)
 * means the credential is dead — clear all auth so the app drops back to public mode instead
 * of error-looping. Only when the request was actually SENT WITH a credential: a signed-out
 * visitor's public-feed request (or our own synthetic `sign_in_required`) has nothing to
 * reject, and reacting to it would just churn auth state.
 */
function reactToError(e: ApiError, requestedBy: string | null): ApiError {
  if (e.status === 401 && requestedBy !== null && !SIGNED_OUT_CODES.has(e.code)) onAuthRejected();
  return e;
}

/** Paths answered locally (no network) — fine signed out. */
const LOCAL_PATHS = new Set(["/config"]);

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<Envelope<T>> {
  const [pathname, rawQuery] = path.split("?");
  // Whose data this is. A request in flight when the credential changes must NEVER resolve
  // into the new user's cache — session-guard cancels what it can, this catches the rest
  // (a response already on the wire). Throwing keeps it out of the cache entirely; the
  // query refetches under the new identity a moment later. Signed out is an identity too
  // (null): public-feed data can't land in a signed-in cache either.
  const requestedBy = authIdentity();
  // Signed out: refuse anything the public feed doesn't carry BEFORE touching the network.
  if (requestedBy === null && !LOCAL_PATHS.has(pathname) && !isPublicAppPath(path)) {
    throw new ApiError(401, SIGN_IN_REQUIRED, "Sign in to see this.", {});
  }
  try {
    const res = await withSignal(signal, () => routeGet<T>(pathname, new URLSearchParams(rawQuery ?? "")));
    if (authIdentity() !== requestedBy) {
      throw new ApiError(409, "identity_changed", "Credential changed while the request was in flight", {});
    }
    return res;
  } catch (err) {
    throw reactToError(toApiError(err), requestedBy);
  }
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  _signal?: AbortSignal,
  headers?: Record<string, string>,
): Promise<Envelope<T>> {
  const [pathname] = path.split("?");
  const requestedBy = authIdentity();
  // Every write (and the contest-state batch read) needs a signed-in user — never public.
  if (requestedBy === null) throw new ApiError(401, SIGN_IN_REQUIRED, "Sign in to do this.", {});
  try {
    return await routePost<T>(pathname, body, headers);
  } catch (err) {
    throw reactToError(toApiError(err), requestedBy);
  }
}
